import type { Ref } from 'vue'
import { formatCitationLabel, parseLegalSectionCitations } from '#shared/legalCitations'
// `LawSectionSearchResult` replaces the local `LawSectionRow`, which was a thirteen-field copy of
// it with almost every member re-declared optional. The endpoint returns the full shape; the
// defensive `|| ''` / `?? null` in `citationDataFromSection` stay because a row can still carry an
// empty string, but the SHAPE is no longer a second, looser opinion about what `/api/legal/read`
// sends.
import type { LawReadResponse, LawSectionSearchResult } from '#shared/laws/lawSearch'
import type { WebChatMessage } from '~/types/web-chat'
import type { ApiFetch } from '~/composables/useApi'
import { mapWith } from '~/stores/entityCache'
import {
  isCitationSnippetData,
  isRecord,
  type CitationSnippetData,
} from '~/composables/web-chat/normalizers'


/**
 * Citation-snippet accumulation for the popover. Operates on the store's shared `citationSnippets`
 * ref so the rendered set stays a single source of truth across streaming, hydration, and the
 * research-run flows. Pure aside from that one ref.
 */
export function useChatCitations(citationSnippets: Ref<Map<string, CitationSnippetData>>) {
  // True while `resolveMessageCitations` is fetching missing snippets from `/api/legal/read`. A counter
  // (not a bare boolean) so overlapping resolves — e.g. a conversation load racing a just-finished turn
  // — only clear the flag once the LAST fetch settles. The popover shows a spinner for any unloaded
  // citation while this is set.
  const pendingResolves = ref(0)
  const citationsResolving = computed(() => pendingResolves.value > 0)

  function addCitationSnippetsToMap(
    citations: Record<string, CitationSnippetData> | undefined,
    target: Map<string, CitationSnippetData>,
  ): boolean {
    if (!citations || Object.keys(citations).length === 0) return false
    let changed = false
    for (const [key, data] of Object.entries(citations)) {
      const sectionId = String(data.sectionId || key || '').trim()
      if (!sectionId) continue
      const existing = target.get(sectionId)
      const nextSnippet = String(data.snippet || '').trim() || existing?.snippet || ''
      target.set(sectionId, {
        sectionId,
        normId: String(data.normId || existing?.normId || ''),
        jurisdiction: data.jurisdiction ?? existing?.jurisdiction ?? null,
        sectionNumber: data.sectionNumber ?? existing?.sectionNumber ?? null,
        displayRef: data.displayRef ?? existing?.displayRef ?? null,
        parentSectionId: data.parentSectionId ?? existing?.parentSectionId ?? null,
        parentSectionType: data.parentSectionType ?? existing?.parentSectionType ?? null,
        parentSectionNumber: data.parentSectionNumber ?? existing?.parentSectionNumber ?? null,
        localSectionType: data.localSectionType ?? existing?.localSectionType ?? null,
        localSectionNumber: data.localSectionNumber ?? existing?.localSectionNumber ?? null,
        canonicalDisplayRef: data.canonicalDisplayRef ?? data.displayRef ?? existing?.canonicalDisplayRef ?? existing?.displayRef ?? null,
        normTitle: String(data.normTitle || data.label || existing?.normTitle || ''),
        label: String(data.label || data.normTitle || existing?.label || ''),
        snippet: nextSnippet,
        fullText: String(data.fullText || existing?.fullText || ''),
      })
      changed = true
    }
    return changed
  }

  function mergeCitationSnippets(
    citations: Record<string, CitationSnippetData> | undefined,
  ) {
    const updated = new Map(citationSnippets.value)
    if (!addCitationSnippetsToMap(citations, updated)) return
    citationSnippets.value = updated
  }

  function quickResearchCitationSnippets(output: unknown): Record<string, CitationSnippetData> {
    const snippets: Record<string, CitationSnippetData> = {}
    const record = isRecord(output) ? output : {}

    // Primary source: the agent packet's `citations` map (sectionId -> CitationSnippetPayload).
    const citations = isRecord(record.citations) ? record.citations : {}
    for (const [key, value] of Object.entries(citations)) {
      if (isCitationSnippetData(value)) {
        snippets[value.sectionId || key] = value
        continue
      }
      if (!isRecord(value)) continue
      const sectionId = typeof value.sectionId === 'string' ? value.sectionId : key
      if (!sectionId) continue
      snippets[sectionId] = {
        sectionId,
        normId: String(value.normId || ''),
        jurisdiction: typeof value.jurisdiction === 'string' ? value.jurisdiction : null,
        sectionNumber: typeof value.sectionNumber === 'string' ? value.sectionNumber : null,
        displayRef: typeof value.displayRef === 'string' ? value.displayRef : null,
        parentSectionId: typeof value.parentSectionId === 'string' ? value.parentSectionId : null,
        parentSectionType: typeof value.parentSectionType === 'string' ? value.parentSectionType : null,
        parentSectionNumber: typeof value.parentSectionNumber === 'string' ? value.parentSectionNumber : null,
        localSectionType: typeof value.localSectionType === 'string' ? value.localSectionType : null,
        localSectionNumber: typeof value.localSectionNumber === 'string' ? value.localSectionNumber : null,
        canonicalDisplayRef: typeof value.canonicalDisplayRef === 'string' ? value.canonicalDisplayRef : typeof value.displayRef === 'string' ? value.displayRef : null,
        normTitle: String(value.normTitle || value.label || ''),
        label: String(value.label || value.normTitle || ''),
        snippet: String(value.snippet || value.fullText || '').slice(0, 240),
        fullText: String(value.fullText || ''),
      }
    }

    // Secondary source: resolved `sources[]` (PublicResearchSource shape) — fills any section the
    // citations map missed and carries the richer display_ref/norm_title for rendering.
    const sources = Array.isArray(record.sources) ? record.sources : []
    for (const source of sources) {
      if (!isRecord(source)) continue
      const sectionId = typeof source.section_id === 'string' ? source.section_id : ''
      if (!sectionId || snippets[sectionId]) continue
      const displayRef = typeof source.display_ref === 'string'
        ? source.display_ref
        : typeof source.section_number === 'string' ? source.section_number : null
      const normTitle = String(source.norm_title || '')
      snippets[sectionId] = {
        sectionId,
        normId: String(source.norm_id || ''),
        jurisdiction: typeof source.jurisdiction === 'string' ? source.jurisdiction : null,
        sectionNumber: typeof source.section_number === 'string' ? source.section_number : null,
        displayRef,
        canonicalDisplayRef: displayRef,
        normTitle,
        label: formatCitationLabel(displayRef, normTitle),
        snippet: String(source.snippet || '').slice(0, 240),
        fullText: String(source.snippet || ''),
      }
    }
    return snippets
  }

  function citationDataFromSection(s: LawSectionSearchResult): CitationSnippetData {
    const displayRef = s.canonicalDisplayRef || s.section_number || s.section_type || null
    const normTitle = String(s.norm_title || '')
    const fullText = String(s.full_text || '')
    return {
      sectionId: s.section_id,
      normId: String(s.norm_id || ''),
      jurisdiction: s.jurisdiction ?? null,
      sectionNumber: s.section_number ?? null,
      displayRef,
      parentSectionId: s.parentSectionId ?? null,
      parentSectionType: s.parentSectionType ?? null,
      parentSectionNumber: s.parentSectionNumber ?? null,
      localSectionType: s.localSectionType ?? null,
      localSectionNumber: s.localSectionNumber ?? null,
      canonicalDisplayRef: displayRef,
      normTitle,
      label: displayRef ? `${displayRef} — ${normTitle}` : normTitle,
      snippet: fullText.slice(0, 240),
      fullText,
    }
  }

  /**
   * THE citation-snippet path: derive the popover snippets from the `cite:section/<id>` markers in the
   * assistant messages themselves, fetching any not yet cached from the DB (`/api/legal/read`). The
   * rendered answer is the source of truth for what's cited, so this ONE resolver serves both live
   * (after a turn streams) and reload — no registry, no markdown scrape. Missing-only + deduped.
   */
  async function resolveMessageCitations(
    messages: WebChatMessage[],
    apiFetch: ApiFetch,
  ): Promise<void> {
    const ids = new Set<string>()
    for (const m of messages) {
      if (m.role !== 'assistant' || !m.content) continue
      for (const c of parseLegalSectionCitations(m.content)) ids.add(c.sectionId)
    }
    const missing = [...ids].filter(id => !citationSnippets.value.has(id))
    if (missing.length === 0) return

    pendingResolves.value++
    try {
      const res = await apiFetch<LawReadResponse>('/api/legal/read', { method: 'POST', body: { ids: missing } })
      const rows = isRecord(res) && Array.isArray(res.results) ? (res.results as LawSectionSearchResult[]) : []
      const map: Record<string, CitationSnippetData> = {}
      const aliases: Array<[cited: string, current: string]> = []
      for (const row of rows) {
        if (!row?.section_id) continue
        map[row.section_id] = citationDataFromSection(row)
        // MAR-962 item 23: the answer cites an id from a superseded import; the read answered with
        // the current section. The popover looks the card up by the CITED id, and the card itself
        // must name the CURRENT section, which is what opening the article navigates to.
        if (row.requested_section_id && row.requested_section_id !== row.section_id) {
          aliases.push([row.requested_section_id, row.section_id])
        }
      }
      mergeCitationSnippets(map)
      for (const [cited, current] of aliases) {
        const card = citationSnippets.value.get(current)
        if (card) citationSnippets.value = mapWith(citationSnippets.value, cited, card)
      }
    } finally {
      pendingResolves.value--
    }
  }

  return {
    addCitationSnippetsToMap,
    mergeCitationSnippets,
    quickResearchCitationSnippets,
    resolveMessageCitations,
    citationsResolving,
  }
}
